import { uiIcon } from "@agent/uiIcons";
import type {
  IconName,
  UiFieldItem,
  UiListItem,
  UiTone,
} from "@agent/uiSchema";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  cn,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  StatusMark,
  type StatusMarkTone,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import type { ReactNode } from "react";

/**
 * Presentational pieces of the a2ui `card` and choice-row `button` primitives.
 * The agent composes the UI itself from a2ui nodes and `UiRenderer` maps node
 * types onto these. They speak the generic action channel (a choice tap or
 * footer button emits its node's `action`, the ✕ emits "dismiss"); the dock
 * decides what each action means. These know only how to look.
 */

interface ToneStyle {
  /** Foreground for a toned value/title/heading. Empty inherits. */
  text: string;
  /** Foreground for a toned icon. Empty inherits. */
  icon: string;
  /** Heading-dot fill, in status ink. The dot never stands alone: it always
      sits beside the heading text, which the same ink colours. */
  dot: string;
  /** Translucent callout tint over an opaque paper base (`cardSeverity`'s
      ink-held-far-back idiom). Empty means untinted. */
  surface: string;
  /** 1px edge in status ink; `null` means the plain hairline (`border-rule`). */
  border: string | null;
  /** The highlighter a worded status (a list row's trailing tag) wears. `null`
      when the tone has no wash: the word takes `text` ink instead, since blue's
      wash means "Brian did this" and a generic info tag makes no such claim. */
  wash: SwipeTone | null;
  /** Icon a toned node falls back to when the payload names none, so a tone is
      never carried by colour alone. `neutral` has no status to announce. */
  toneIcon: IconName | undefined;
}

// Single source of truth for tone -> look, on the highlighter system: each
// status tone is one marker out of the drawer, wash for words and ink for
// everything else. `success` takes the working green, `warning` the flag
// yellow, `urgent` the stopped red. `info` is generic emphasis, not "Brian
// did this", so it takes the indigo accent (`accent-deep` for AA small text)
// and never the Brian-scoped blue. `neutral` stays blank so untoned content
// keeps whatever its context already gives it.
const TONE_STYLES: Record<UiTone, ToneStyle> = {
  neutral: {
    text: "",
    icon: "",
    dot: "",
    surface: "",
    border: null,
    wash: null,
    toneIcon: undefined,
  },
  info: {
    text: "text-accent-deep",
    icon: "text-accent-deep",
    dot: "bg-accent",
    surface: "bg-accent/8",
    border: "border-accent",
    wash: null,
    toneIcon: "circle-info",
  },
  success: {
    text: "text-ink-working",
    icon: "text-ink-working",
    dot: "bg-ink-working",
    surface: "bg-ink-working/8",
    border: "border-ink-working",
    wash: "working",
    toneIcon: "circle-check",
  },
  warning: {
    text: "text-ink-flag",
    icon: "text-ink-flag",
    dot: "bg-ink-flag",
    surface: "bg-ink-flag/8",
    border: "border-ink-flag",
    wash: "flag",
    toneIcon: "triangle-exclamation",
  },
  urgent: {
    text: "text-ink-stopped",
    icon: "text-ink-stopped",
    dot: "bg-ink-stopped",
    surface: "bg-ink-stopped/8",
    border: "border-ink-stopped",
    wash: "stopped",
    toneIcon: "circle-exclamation",
  },
};

/** The design-system look for a node's tone. Unknown/absent reads as `neutral`. */
export const toneStyle = (tone: UiTone | undefined): ToneStyle =>
  TONE_STYLES[tone ?? "neutral"] ?? TONE_STYLES.neutral;

/** A catalogue icon by name. Renders nothing for a name this build lacks. */
export const UiIcon = ({
  name,
  className,
}: {
  name: IconName | undefined;
  className?: string;
}) => {
  const icon = uiIcon(name);
  if (!icon) return null;
  return <FontAwesomeIcon icon={icon} className={className} aria-hidden />;
};

/**
 * Status chrome for the root card, named for the emphasis an a2ui payload asks
 * for. The vocabulary is wider than the product uses: only the severity pair
 * takes chrome, a status ring outside the card's own hairline (`TaskIndexCard`'s
 * idiom), and everything else is a calm, clean card. `severityVariant` in
 * `cardSeverity` is the one producer.
 */
export type UiCardVariant =
  | "default"
  | "modal"
  | "danger"
  | "warning"
  | "success"
  | "info"
  | "ghost";

// Two strokes and the wash for warning, as on TaskIndexCard and the room
// tiles: flag ink reads brown at ring width, and the wash needs the width.
const CARD_VARIANT_RING: Partial<Record<UiCardVariant, string>> = {
  warning: "ring-2 ring-wash-flag",
  danger: "ring-2 ring-ink-stopped",
};

export const CardFrame = ({
  id,
  dismissible,
  onDismiss,
  children,
  badge,
  variant = "default",
}: {
  id: string;
  dismissible: boolean;
  onDismiss: () => void;
  children: ReactNode;
  /** Optional status badge stamped at the top of the card (e.g. "Approved" on a
      resolved estimate). Sits above the card's content as its own banner row. */
  badge?: ReactNode;
  /** Card status chrome. Defaults to `default` (untinted); a surfaced view
      passes its severity variant so the card gets the same watch/urgent ring
      task cards have (WEB-3251/BE-1918). */
  variant?: UiCardVariant;
}) => (
  // Opaque card stock, so the same tree reads identically in the index card's
  // recessed band well and standing on any other surface it is dropped onto.
  <div
    data-testid={`ui-card-${id}`}
    className={cn(
      "rounded-paper border border-rule bg-card p-3 font-body text-ink-body shadow-lift",
      CARD_VARIANT_RING[variant],
    )}
  >
    {/* The ✕ floats in the top-right corner (rather than taking its own row) so
        the first child — typically the heading — shares its line. `pr-8` keeps
        that content clear of the button. */}
    <div className="relative">
      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid={`ui-dismiss-${id}`}
          className="absolute right-0 top-0 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-annotation transition-colors duration-150 ease-paper hover:bg-recess hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-reduce:transition-none"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
      <div className={cn("flex flex-col gap-2", dismissible && "pr-8")}>
        {badge && <div className="flex">{badge}</div>}
        {children}
      </div>
    </div>
  </div>
);

/**
 * A `grid` node: `columns` side by side once there's room, one column when
 * there isn't. Collapse is a container query, not a viewport breakpoint,
 * since the same tree renders in both the narrow dock and the wide reader.
 */
export const GridBlock = ({
  columns,
  children,
}: {
  columns: 2 | 3;
  children: ReactNode;
}) => (
  <div className="@container" data-testid={`ui-grid-${columns}`}>
    {/* Spelled out per count: Tailwind only emits classes literal in source. */}
    <div
      className={
        columns === 3
          ? "grid grid-cols-1 gap-3 @md:grid-cols-2 @2xl:grid-cols-3"
          : "grid grid-cols-1 gap-3 @md:grid-cols-2"
      }
    >
      {children}
    </div>
  </div>
);

/**
 * The title row of a `section` or `callout`. One component rather than a shared
 * class string so both render a real `h3` — a callout's title is as much a
 * heading as a section's, and heading navigation has to find both.
 *
 * Only a TITLED panel gets the `h3`: an icon-only row stays a `div`, since a
 * heading whose sole child is an aria-hidden icon has empty text content and
 * puts a blank stop in the heading outline (axe `empty-heading`).
 *
 * Always text-ink, never the tone's colour: differently-toned titles on one
 * card read as unrelated headings, so tone shows on the icon/border instead.
 * Renders nothing when there is neither a title nor an icon.
 */
const PANEL_TITLE_ROW =
  "flex items-center gap-2 pb-1 font-display text-lg font-bold text-ink";

const PanelTitle = ({
  title,
  icon,
  iconClassName,
}: {
  title?: string;
  icon?: IconName;
  iconClassName: string;
}) => {
  const iconNode = (
    <UiIcon name={icon} className={cn("shrink-0 text-base", iconClassName)} />
  );
  if (!title) {
    return icon ? <div className={PANEL_TITLE_ROW}>{iconNode}</div> : null;
  }
  return (
    <h3 className={PANEL_TITLE_ROW}>
      {iconNode}
      <span className="min-w-0">{title}</span>
    </h3>
  );
};

/** A `section` node: a titled panel within a card, a page laid on the card
    stock. A toned section says so on its edge and icon, never as a fill. */
export const PanelSection = ({
  title,
  icon,
  tone,
  children,
}: {
  title?: string;
  icon?: IconName;
  tone?: UiTone;
  children: ReactNode;
}) => {
  const style = toneStyle(tone);
  return (
    <section
      data-testid="ui-section"
      className={cn(
        "flex flex-col gap-2 rounded-paper border bg-paper p-3",
        style.border ?? "border-rule",
      )}
    >
      <PanelTitle
        title={title}
        icon={icon ?? style.toneIcon}
        iconClassName={style.icon || "text-annotation"}
      />
      {children}
    </section>
  );
};

/**
 * A `callout` node: the toned block on a card meant to be read first. The tint
 * is the status ink held far back (`/8`) over its own opaque `bg-card` base,
 * not the card's surface, so it holds its hue whatever the host backdrop and
 * never compounds with a `watch`/`urgent` card's ring.
 */
export const Callout = ({
  tone,
  title,
  icon,
  children,
}: {
  tone: UiTone;
  title?: string;
  icon?: IconName;
  children: ReactNode;
}) => {
  const style = toneStyle(tone);
  return (
    <div
      data-testid={`ui-callout-${tone}`}
      className={cn(
        "overflow-hidden rounded-paper border bg-card",
        style.border ?? "border-rule",
      )}
    >
      <div className={cn("flex flex-col gap-2 p-3", style.surface)}>
        <PanelTitle
          title={title}
          icon={icon ?? style.toneIcon}
          iconClassName={style.icon || "text-annotation"}
        />
        {children}
      </div>
    </div>
  );
};

const toCell = (value: unknown): string =>
  typeof value === "string" ? value : value == null ? "" : String(value);

/**
 * A `fields` node: label left, value right, hairline-separated rows. Labels are
 * mono small-caps annotation, values the mono reading beside them. A value's
 * `tone` inks the value, and puts the tone's icon beside it when the payload
 * names none, so a flagged reading is never signalled by colour alone. The icon
 * sits in the VALUE cell, next to what the tone colours, so labels keep a
 * shared left edge whether or not a row is flagged. `note` gets its own
 * full-width line below the pair, left-aligned at the label's edge, since
 * right-aligned under the value it read as floating into the next row.
 * Defensive about shape, like `TableBlock`, so a bad payload can't crash the card.
 */
export const FieldList = ({ items }: { items: UiFieldItem[] }) => {
  const rows = (Array.isArray(items) ? items : []).filter(
    (item): item is UiFieldItem => item != null && typeof item === "object",
  );
  if (rows.length === 0) return null;

  return (
    <dl data-testid="ui-fields" className="flex flex-col">
      {rows.map((item, i) => {
        const style = toneStyle(item.tone);
        return (
          <div
            key={i}
            data-testid={`ui-field-${i}`}
            className={cn(
              "grid grid-cols-[1fr_auto] items-baseline gap-x-3 py-2",
              i > 0 && "border-t border-rule",
            )}
          >
            <dt className="min-w-0 text-annotation-caps text-annotation">
              {toCell(item.label)}
            </dt>
            <dd
              className={cn(
                "flex items-baseline justify-end gap-2 text-right font-mono text-sm font-medium",
                style.text || "text-ink",
              )}
            >
              <UiIcon
                name={item.icon ?? style.toneIcon}
                className={cn("shrink-0", style.icon || "text-annotation")}
              />
              <span className="min-w-0">{toCell(item.value)}</span>
            </dd>
            {item.note && (
              <dd className="col-span-2 pt-0.5 text-xs text-annotation">
                {toCell(item.note)}
              </dd>
            )}
          </div>
        );
      })}
    </dl>
  );
};

/**
 * A `list` node: one row per item with an optional icon and trailing tag.
 * `divided` adds hairlines, for a list read row by row (alerts, reminders).
 *
 * A toned row that names no icon takes the tone's own, so a row's status never
 * rests on colour alone. The trailing tag is a worded status, so it wears the
 * row's highlighter (the wash rule: a wash always carries its word); a tone
 * with no wash of its own says the word in its ink instead.
 */
export const ItemList = ({
  items,
  divided = false,
}: {
  items: UiListItem[];
  divided?: boolean;
}) => {
  const rows = (Array.isArray(items) ? items : []).filter(
    (item): item is UiListItem => item != null && typeof item === "object",
  );
  if (rows.length === 0) return null;

  return (
    <ul
      data-testid="ui-list"
      className={cn("flex flex-col", !divided && "gap-2")}
    >
      {rows.map((item, i) => {
        const style = toneStyle(item.tone);
        return (
          <li
            key={i}
            data-testid={`ui-list-item-${i}`}
            className={cn(
              "flex items-start gap-2",
              divided && "py-2",
              divided && i > 0 && "border-t border-rule",
            )}
          >
            <UiIcon
              name={item.icon ?? style.toneIcon}
              className={cn("mt-0.5 shrink-0", style.icon || "text-annotation")}
            />
            <span className="min-w-0 flex-1 text-sm text-ink-body">
              {toCell(item.text)}
            </span>
            {item.badge &&
              (style.wash ? (
                <Swipe
                  tone={style.wash}
                  data-testid={`ui-list-badge-${i}`}
                  className="inline-flex shrink-0 items-center text-annotation-caps"
                >
                  {toCell(item.badge)}
                </Swipe>
              ) : (
                <span
                  data-testid={`ui-list-badge-${i}`}
                  className={cn(
                    "shrink-0 text-annotation-caps",
                    style.text || "text-annotation",
                  )}
                >
                  {toCell(item.badge)}
                </span>
              ))}
          </li>
        );
      })}
    </ul>
  );
};

/** A `divider` node: a hairline rule between blocks of a card. */
export const DividerRule = () => (
  <hr data-testid="ui-divider" className="border-t border-rule" />
);

/**
 * A `badge` node's look. The payload speaks in colour names; each one lands on
 * the marker that means it (a worded status wearing its wash) except `blue`,
 * whose wash is scoped to "Brian did this" and which a generic payload tag
 * can't claim: it says its word in the indigo accent ink instead, as does an
 * unnamed variant (the old brand fallback). `gray` is a quiet annotation.
 */
const BADGE_LOOK: Record<
  string,
  { wash: SwipeTone | null; mark: StatusMarkTone; look: string }
> = {
  red: { wash: "stopped", mark: "neutral", look: "stopped" },
  orange: { wash: "flag", mark: "neutral", look: "flag" },
  yellow: { wash: "flag", mark: "neutral", look: "flag" },
  green: { wash: "working", mark: "neutral", look: "working" },
  blue: { wash: null, mark: "accent", look: "accent" },
  gray: { wash: null, mark: "neutral", look: "neutral" },
  default: { wash: null, mark: "accent", look: "accent" },
};

/** A `badge` node: an inert status word (e.g. a summary alert's "Safety" tag).
    `data-variant` carries the resolved look as a stable test hook. */
export const UiBadge = ({
  variant,
  icon,
  text,
  dataTestId,
}: {
  variant: string | undefined;
  icon?: IconName;
  text: string;
  dataTestId: string;
}) => {
  const look = BADGE_LOOK[variant ?? "default"] ?? BADGE_LOOK.default;
  // Gated on the glyph actually resolving: an unknown icon name renders
  // nothing, and an empty icon slot would still take its gap in the row.
  const glyph = uiIcon(icon) ? <UiIcon name={icon} /> : undefined;

  return look.wash ? (
    <Swipe
      tone={look.wash}
      icon={glyph}
      data-testid={dataTestId}
      data-variant={look.look}
      className="shrink-0 text-annotation-caps"
    >
      {text}
    </Swipe>
  ) : (
    <StatusMark
      tone={look.mark}
      icon={glyph}
      data-testid={dataTestId}
      data-variant={look.look}
      className="shrink-0"
    >
      {text}
    </StatusMark>
  );
};

/**
 * A static `table` node — a header from `columns` and one row per `rows` entry,
 * on green-bar ledger paper: banded rows built for scanning long columns.
 * Display-only: no binding, no actions. Defensive about shape since a legacy or
 * odd payload shouldn't hard-fail the surrounding card — ragged rows pad to the
 * widest row, and a table with no cells renders nothing.
 */
export const TableBlock = ({
  columns,
  rows,
}: {
  columns: string[];
  rows: string[][];
}) => {
  const header = Array.isArray(columns) ? columns.map(toCell) : [];
  const body = (Array.isArray(rows) ? rows : [])
    .filter(Array.isArray)
    .map((row) => row.map(toCell));
  const colCount = Math.max(header.length, 0, ...body.map((row) => row.length));

  if (colCount === 0) return null;

  return (
    <div data-testid="ui-table" className="min-w-0">
      <LedgerTable>
        {header.length > 0 && (
          <LedgerHead>
            <tr>
              {Array.from({ length: colCount }, (_, i) => (
                <LedgerHeadCell key={i}>{header[i] ?? ""}</LedgerHeadCell>
              ))}
            </tr>
          </LedgerHead>
        )}
        <LedgerBody>
          {body.map((row, ri) => (
            <LedgerRow key={ri}>
              {Array.from({ length: colCount }, (_, ci) => (
                <LedgerCell key={ci}>{row[ci] ?? ""}</LedgerCell>
              ))}
            </LedgerRow>
          ))}
        </LedgerBody>
      </LedgerTable>
    </div>
  );
};

/** A `button` node with a `payload` or `description` — a tappable choice row.
    Interactive paper: it lifts on hover, it doesn't recolor. */
export const OptionButton = ({
  text,
  description,
  disabled,
  dataTestId,
  onPress,
}: {
  text: string;
  description?: string;
  disabled: boolean;
  dataTestId: string;
  onPress: () => void;
}) => (
  <button
    type="button"
    onClick={onPress}
    disabled={disabled}
    data-testid={dataTestId}
    className={cn(
      "flex w-full items-center justify-between gap-2 rounded-paper border border-rule px-3 py-2 text-left",
      "transition duration-150 ease-paper motion-reduce:transition-none",
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
      // `cn` has no tailwind-merge, so the surface is picked here rather than
      // letting a disabled recess fight the resting card in stylesheet order.
      disabled
        ? "cursor-not-allowed bg-recess"
        : "bg-card shadow-lift hover:-translate-y-px hover:shadow-lift-lg",
    )}
  >
    <span className="flex min-w-0 flex-col">
      <span
        className={cn(
          "font-body text-sm font-medium",
          disabled ? "text-faint" : "text-ink",
        )}
      >
        {text}
      </span>
      {description && (
        <span
          className={cn("text-sm", disabled ? "text-faint" : "text-annotation")}
        >
          {description}
        </span>
      )}
    </span>
  </button>
);
